<template>
  <section id="user-profile">
    <b-overlay
      :show="loading"
      no-center
      variant="transparent"
      rounded="sm"
    >
      <template #overlay>
        <loading-spinner />
      </template>
      <div
        v-if="!notLoaded"
        class="pv-page"
      >
        <!-- Üst bar: geri + menü -->
        <div class="pv-topbar">
          <button
            type="button"
            class="pv-round-btn"
            @click="back"
          >
            <font-awesome-icon
              icon="fa-solid fa-arrow-left"
              size="lg"
            />
          </button>
        </div>

        <div
          v-if="user"
          class="pv-layout"
        >
          <!-- ===== Sağ: bilgiler ===== -->
          <div class="pv-info-col">
            <!-- Hero kartı -->
            <div class="pv-card pv-hero">
              <div
                class="pv-hero-bg"
                aria-hidden="true"
              >
                <div class="pv-hero-glow" />
                <svg
                  class="pv-heart pv-heart-big"
                  viewBox="-12 -12 124 124"
                >
                  <defs>
                    <linearGradient
                      id="pvHeartGrad"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop
                        offset="0%"
                        stop-color="#ff2ea6"
                        stop-opacity="0.95"
                      />
                      <stop
                        offset="55%"
                        stop-color="#ea03b2"
                        stop-opacity="0.6"
                      />
                      <stop
                        offset="100%"
                        stop-color="#7928ca"
                        stop-opacity="0.25"
                      />
                    </linearGradient>
                  </defs>
                  <path
                    d="M50,88 C20,62 5,42 5,26 C5,12 16,3 29,3 C38,3 46,8 50,16 C54,8 62,3 71,3 C84,3 95,12 95,26 C95,42 80,62 50,88 Z"
                    fill="none"
                    stroke="url(#pvHeartGrad)"
                    stroke-width="6.5"
                  />
                </svg>
                <svg
                  class="pv-heart pv-heart-small"
                  viewBox="-6 -6 36 36"
                >
                  <path
                    d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                    fill="url(#pvHeartGrad)"
                  />
                </svg>
              </div>

              <!-- Seçenekler (engelle / şikayet et): kartın sağ üst köşesinde -->
              <div
                v-if="!owner && user"
                class="profile-menu pv-hero-menu"
              >
                <b-dropdown
                  variant="link"
                  no-caret
                  toggle-class="p-0"
                  menu-class="pv-dropdown-menu"
                  right
                >
                  <template #button-content>
                    <span
                      class="pv-hero-menu-btn"
                      :title="$t('Options')"
                    >
                      <feather-icon
                        icon="MoreVerticalIcon"
                        size="20"
                      />
                    </span>
                  </template>
                  <b-dropdown-item
                    v-if="!user.blockedByUser"
                    @click="blockUser"
                  >
                    <feather-icon
                      icon="SlashIcon"
                      size="15"
                      class="mr-50"
                    />
                    {{ $t('Block user') }}
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-else
                    @click="unBlockUser"
                  >
                    <feather-icon
                      icon="CheckCircleIcon"
                      size="15"
                      class="mr-50"
                    />
                    {{ $t('Unblock') }}
                  </b-dropdown-item>
                  <b-dropdown-item @click="showReportModal">
                    <feather-icon
                      icon="FlagIcon"
                      size="15"
                      class="mr-50"
                    />
                    {{ $t('Report') }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
              <div class="pv-hero-content">
                <div class="pv-avatar-wrap">
                  <button
                    type="button"
                    class="pv-avatar"
                    :title="$t('Photos')"
                    @click="openPhotoModal"
                  >
                    <img
                      v-if="user.profile.image"
                      :src="user.profile.image"
                      :alt="user.userName"
                    >
                    <feather-icon
                      v-else
                      icon="UserIcon"
                      size="56"
                    />
                    <span
                      v-if="userImages.length > 1"
                      class="pv-avatar-count"
                    >
                      <feather-icon
                        icon="ImageIcon"
                        size="12"
                      />
                      {{ userImages.length }}
                    </span>
                  </button>
                  <button
                    v-if="owner"
                    type="button"
                    class="pv-avatar-edit"
                    :title="$t('Edit your medias')"
                    @click="$emit('open-modal-gallery')"
                  >
                    <feather-icon
                      icon="Edit2Icon"
                      size="15"
                    />
                  </button>
                </div>

                <div class="pv-status-row online-status">
                  <status-indicator
                    v-if="!user.streamInfo && !user.voiceRoomInfo"
                    :user="user"
                    class="online-status"
                  />
                  <div
                    v-if="user.streamInfo || user.voiceRoomInfo"
                    class="live-badge-wrapper"
                  >
                    <span
                      class="live-badge"
                      @click="goToStream"
                    >
                      <svg v-if="user.streamInfo" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="feather feather-video"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>
                      <svg v-else-if="user.voiceRoomInfo" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="feather feather-mic"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>
                      Live
                    </span>
                  </div>
                  <span
                    v-if="user.isAi !== undefined"
                    class="content-type-badge"
                    :class="user.isAi ? 'ai' : 'real'"
                  >
                    {{ user.isAi ? 'AI' : 'Real' }}
                  </span>
                </div>

                <h2 class="pv-name">
                  {{ user.userName }}<span
                    v-if="user.profile.year"
                    class="pv-age"
                  >, {{ new Date().getFullYear() - user.profile.year }}</span>
                </h2>
                <div
                  v-if="user && user.identifier && owner"
                  class="pv-meta"
                >
                  ID: {{ user.identifier }}
                </div>
                <div
                  v-if="user.profile.country"
                  class="pv-meta pv-location"
                >
                  <feather-icon
                    icon="MapPinIcon"
                    size="16"
                  />
                  {{ user.profile.country }}
                </div>

                <!-- Aksiyonlar -->
                <div
                  v-if="!owner"
                  class="pv-actions"
                >
                  <!-- Keşfet kartlarındaki buton düzeni: yazılı arama hap butonları + yuvarlak sohbet / beğeni -->
                  <!-- Keşfet kartlarıyla aynı kural: tek arama butonu (cameraOn -> görüntülü, değilse voiceOn -> sesli) -->
                  <button
                    v-if="profileCallMode === 'voice'"
                    type="button"
                    class="pv-action pv-action-pill pv-action-phone"
                    :title="$t('Voice Call')"
                    @click="call"
                  >
                    <feather-icon
                      icon="PhoneIcon"
                      size="18"
                    />
                    <span>{{ $t('Voice Call') }}</span>
                  </button>
                  <button
                    v-else-if="profileCallMode === 'video'"
                    type="button"
                    class="pv-action pv-action-pill pv-action-video"
                    :title="$t('Video Call')"
                    @click="videoCall"
                  >
                    <feather-icon
                      icon="VideoIcon"
                      size="18"
                    />
                    <span>{{ $t('Video Call') }}</span>
                  </button>
                  <router-link
                    :to="{name: 'chat', params: {userId: user.id}}"
                    class="pv-action pv-action-chat"
                    :title="$t('Send a message')"
                    @click.native="gtmGoChat"
                  >
                    <feather-icon
                      icon="MessageCircleIcon"
                      size="20"
                    />
                  </router-link>
                  <button
                    type="button"
                    class="pv-action pv-action-like"
                    :class="{ active: user.fav }"
                    :title="$t('Like')"
                    @click="user.fav ? unlike() : like()"
                  >
                    <font-awesome-icon :icon="user.fav ? ['fas', 'heart'] : ['far', 'heart']" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Hakkımda -->
            <div class="pv-card pv-about">
              <div class="pv-section-head">
                <div class="pv-section-title">
                  <span class="pv-section-icon">
                    <feather-icon
                      icon="UserIcon"
                      size="18"
                    />
                  </span>
                  <h3>{{ $t('About me') }}</h3>
                </div>
                <router-link
                  v-if="owner"
                  :to="{name: 'settings.profile-information'}"
                  class="pv-round-btn small"
                >
                  <feather-icon
                    icon="Edit2Icon"
                    size="15"
                  />
                </router-link>
              </div>
              <p v-if="owner">
                {{ user.profile.description && user.profile.description !== ''
                  ? user.profile.description
                  : $t('You haven\'t added any information about me yet') }}
              </p>
              <p v-else>
                {{ user.profile.description && user.profile.description !== ''
                  ? user.profile.description
                  : $t('User has not added information about') }}
              </p>

              <div
                v-if="owner && canUploadBonding"
                class="bonding-upload-entry"
              >
                <b-button
                  variant="primary"
                  block
                  @click="$emit('open-bonding-upload')"
                >
                  {{ $t('Start Bonding') }}
                </b-button>
                <div class="bonding-upload-entry-subtitle">
                  {{ $t('(minimum 20 seconds)') }}
                </div>
              </div>
            </div>

            <!-- Seviyeler -->
            <div
              v-if="user && user.profile.gender === 'male'"
              class="pv-card pv-levels"
            >
              <div
                v-for="level in [
                  { key: 'member', label: 'BOHO MEMBER', icon: iconLevelMember },
                  { key: 'bronze', label: 'BRONZE', icon: iconLevelBronze },
                  { key: 'silver', label: 'SILVER', icon: iconLevelSilver },
                  { key: 'gold', label: 'GOLD', icon: iconLevelGold },
                ]"
                :key="level.key"
                class="pv-level"
                :class="{ passive: checkBadge(level.key) }"
              >
                <div class="pv-level-head">
                  <span class="pv-level-icon">
                    <img
                      :src="level.icon"
                      :alt="level.label"
                    >
                  </span>
                  <span class="pv-level-label">{{ level.label }}</span>
                </div>
                <div class="pv-level-bar">
                  <span />
                </div>
              </div>
            </div>

            <!-- Moments -->
            <div class="pv-card pv-moments">
              <div class="pv-section-head">
                <div class="pv-section-title">
                  <span class="pv-section-icon pink">
                    <feather-icon
                      icon="ImageIcon"
                      size="18"
                    />
                  </span>
                  <h3>{{ $t('Moments') }}</h3>
                </div>
              </div>
              <div
                v-if="momentsLoading && moments.length === 0"
                class="text-center py-3"
              >
                <b-spinner variant="primary" />
              </div>
              <div
                v-else-if="moments.length > 0"
                class="pv-moments-grid"
              >
                <div
                  v-for="moment in moments"
                  :key="moment.id"
                  class="pv-moment"
                  @click="openMomentModal(moment)"
                >
                  <template v-if="isVideo(moment.media_type)">
                    <video
                      :src="moment.media_url"
                      muted
                      preload="metadata"
                    />
                    <span class="pv-moment-play">
                      <font-awesome-icon icon="fa-solid fa-play" />
                    </span>
                  </template>
                  <b-img
                    v-else
                    :src="moment.media_url"
                  />
                  <span class="pv-moment-likes">
                    <font-awesome-icon icon="fa-solid fa-heart" />
                    {{ moment.like_count || 0 }}
                  </span>
                </div>
              </div>
              <div
                v-else
                class="pv-moments-empty"
              >
                <feather-icon
                  icon="GiftIcon"
                  size="40"
                />
                <span>{{ $t('No moments yet') }}</span>
              </div>
              <div
                v-if="momentsHasMore && moments.length > 0"
                class="text-center mt-2"
              >
                <button
                  type="button"
                  class="pv-load-more"
                  :disabled="momentsLoading"
                  @click="getMoments()"
                >
                  <b-spinner
                    v-if="momentsLoading"
                    small
                    class="mr-1"
                  />
                  {{ $t('Load more') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <b-alert
        v-else
        variant="primary"
        show
      >
        <div class="alert-body">
          <span>{{ $t('User not found') }}!</span>
        </div>
      </b-alert>

      <!-- Hediye satışı kaldırıldı — hediye gönderme modalı devre dışı
<b-modal
        v-if="selectedGift"
        v-model="giftModal"
        centered
        hide-header-close
        hide-footer
        size="sm"
        :title="$t('Gift for {name}', {name: user.userName})"
      >
        <div class="gifts-modal">
          <h4>{{ selectedGift.label }}</h4>
          <b-img
            :src="getImgGift(selectedGift.image)"
            width="128"
            :alt="selectedGift.label"
          />
          <h4 v-if="userGender === 'male'">
            {{ numberFormat(selectedGift.credits) }}
            <b-img
              :src="iconDiamonds1"
              width="16"
              height="16"
              style="margin-top: -2px"
            />
          </h4>
          <b-button
            variant="primary"
            block
            :disabled="loading"
            @click="sendGift(selectedGift)"
          >
            <b-spinner
              v-if="loading"
              small
            />
            {{ $t('Send') }}
          </b-button>
          <b-button
            variant="outline-dark"
            size="sm"
            :disabled="loading"
            @click="hideGiftModal(true)"
          >
            {{ $t('Choose Another Gift') }}
          </b-button>
        </div>
      </b-modal>
      -->

      <!-- Hediye satışı kaldırıldı — hediye seçme modalı devre dışı
<b-modal
        v-if="gifts.length > 0"
        v-model="chooseGiftModal"
        body-class="choose-gift-modal-body"
        scrollable
        hide-footer
        size="lg"
      >
        <div class="choose-gift-modal">
          <div
            v-for="gift in gifts"
            :key="gift.productId"
            class="gift"
          >
            <b-img
              width="48"
              :alt="gift.label"
              :src="getImgGift(gift.image)"
              class="gift-image"
              @click="showGiftModal(gift)"
            />
            <p v-if="userGender === 'male'" class="gift-price">
              <b-img
                :src="iconDiamonds1"
                width="16"
                height="16"
                style="margin-top: -2px"
              />
              {{ numberFormat(gift.credits) }}
            </p>
          </div>
        </div>
      </b-modal>
      -->

      <b-modal
        v-if="user"
        v-model="modalReportUser"
        :title="$t('Report {name}', {name: user.userName})"
        centered
        hide-header-close
        hide-footer
      >
        <div>
          <validation-observer
            v-if="reportReasonTypes.length"
            ref="reportForm"
            #default="{invalid}"
          >
            <b-form
              class="mt-2"
              @submit.prevent
            >

              <b-form-group
                label-for="reason"
                :label="$t('Report reason')"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Report reason')"
                  vid="reason"
                  rules="required"
                >
                  <v-select
                    v-model="report.reason"
                    :clearable="false"
                    :disabled="loading"
                    label="title"
                    :options="reportReasonTypes"
                    :reduce="option => option.value"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group
                v-if="report.reason === 'Other'"
                label-for="reportMessage"
                :label="$t('Your message')"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Your message')"
                  vid="description"
                  rules="required"
                >
                  <b-form-textarea
                    id="description"
                    v-model="report.description"
                    :disabled="loading"
                    :placeholder="$t('Your message')"
                    rows="3"
                    max-rows="6"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <div class="d-flex justify-content-between">
                <b-button
                  v-if="false"
                  variant="outline-secondary"
                  class="mt-2 px-4 mb-6"
                  :disabled="loading"
                  @click="modalReportUser = false"
                >
                  {{ $t('Cancel') }}
                </b-button>
                <b-button
                  variant="primary"
                  class="mt-2 px-4 mb-6"
                  :disabled="loading"
                  @click="reportUser"
                >
                  <b-spinner
                    v-if="loading"
                    small
                  />
                  {{ $t('Report') }}
                </b-button>
              </div>
            </b-form>
          </validation-observer>
        </div>
      </b-modal>

      <b-modal
        v-if="creditsMedia"
        v-model="modalBuyMedia"
        centered
        hide-header-close
        hide-footer
        size="sm"
        :title="$t('Buy media')"
      >
        <div class="gifts-modal">
          <h4
            v-if="creditsMedia.type == 1"
            class="text-center"
          >
            {{ $t('Buy photo notification message', {name: user.userName, credits: creditsPhoto.credits}) }}
          </h4>
          <h4
            v-else
            class="text-center"
          >
            {{ $t('Buy video notification message', {name: user.userName, credits: creditsVideo.credits}) }}
          </h4>
          <b-button
            variant="primary"
            block
            :disabled="loading"
            @click="buyMedia(creditsMedia)"
          >
            {{ $t('Buy') }}
          </b-button>
          <b-button
            variant="outline-dark"
            size="sm"
            :disabled="loading"
            @click="modalBuyMedia = false; creditsMedia = null; "
          >
            {{ $t('Cancel') }}
          </b-button>
        </div>
      </b-modal>

      <!-- Profil fotoğrafları (büyük görünüm) -->
      <b-modal
        v-if="user"
        v-model="photoModal"
        modal-class="pv-photo-modal"
        size="lg"
        centered
        hide-header
        hide-footer
      >
        <div class="pv-photo-modal-inner">
          <button
            type="button"
            class="pv-photo-close"
            @click="photoModal = false"
          >
            <feather-icon
              icon="XIcon"
              size="20"
            />
          </button>
          <b-carousel
            id="user-profile-carousel"
            v-model="slide"
            :interval="0"
            controls
            indicators
            fade
            @sliding-start="onSlideStart"
            @sliding-end="onSlideEnd"
          >
            <b-carousel-slide
              v-for="image in userImages"
              :key="'carousel-' + image.id"
              :img-src="image.secureUrl"
            >
              <template #img>
                <div class="pv-photo-slide">
                  <b-img
                    :src="image.secureUrl"
                    :alt="user.userName"
                  />
                </div>
              </template>
            </b-carousel-slide>
          </b-carousel>
        </div>
      </b-modal>

      <!-- Moment Detail Modal -->
      <b-modal
        v-if="selectedMoment"
        v-model="momentModal"
        modal-class="moment-detail-modal"
        size="lg"
        centered
        hide-header
        hide-footer
        @hidden="closeMomentModal"
      >
        <div class="moment-detail">
          <div class="moment-detail-close" @click="closeMomentModal">
            <feather-icon icon="XIcon" size="20" />
          </div>
          <div class="moment-detail-media">
            <video
              v-if="isVideo(selectedMoment.media_type)"
              :src="selectedMoment.media_url"
              controls
              autoplay
              class="moment-detail-video"
            />
            <b-img
              v-else
              :src="selectedMoment.media_url"
              class="moment-detail-image"
            />
          </div>
          <div class="moment-detail-info">
            <div class="moment-detail-content" v-if="selectedMoment.content">
              {{ selectedMoment.content }}
            </div>
            <div class="moment-detail-stats">
              <span class="like-stat" @click="toggleMomentLike(selectedMoment)">
                <font-awesome-icon
                  icon="fa-solid fa-heart"
                  :class="{ 'liked': selectedMoment.is_liked }"
                />
                {{ selectedMoment.like_count || 0 }}
              </span>
              <span
                v-if="owner"
                class="delete-stat"
                @click="deleteMoment(selectedMoment)"
              >
                <feather-icon icon="Trash2Icon" size="18" />
                {{ $t('Delete') }}
              </span>
            </div>
            <div class="moment-message-input">
              <input
                v-model="momentMessageText"
                type="text"
                class="message-input-field"
                :placeholder="$t('Send a message') + '...'"
                @keyup.enter="sendMomentMessage"
              >
              <div
                class="send-btn"
                :class="{ 'disabled': !momentMessageText.trim() }"
                @click="sendMomentMessage"
              >
                <font-awesome-icon icon="fa-solid fa-paper-plane" />
              </div>
            </div>
          </div>
        </div>
      </b-modal>

    </b-overlay>
  </section>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import 'viewerjs/dist/viewer.css'
import {
  BAlert, BButton, BCard, BCardBody, BCarousel, BCarouselSlide, BCol, BDropdown, BDropdownItem, BEmbed, BForm,
  BFormGroup, BFormTextarea, BImg, BModal, BOverlay, BRow, BSpinner,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
// eslint-disable-next-line import/extensions
import LoadingSpinner from '@/components/LoadingSpinner'
// eslint-disable-next-line import/extensions
import StatusIndicator from '@/components/StatusIndicator'
import {
  gtmTrackNewEvent, showNotify, throwDefaultError, numberFormat,
} from '@/helpers/helpers'
import store from '@/store'
import vSelect from 'vue-select'
import { subscribeVideoRoomService } from '@/services/VideoRoomService'
import { subscribeVoiceRoom } from '@/services/VoiceRoomService'
import { logEvent } from 'firebase/analytics'

// import { getUser } from '@/auth/utils'

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCarousel,
    BCarouselSlide,
    BCol,
    BDropdown,
    BDropdownItem,
    BEmbed,
    BForm,
    BFormGroup,
    BFormTextarea,
    BImg,
    BModal,
    BOverlay,
    BRow,
    BSpinner,

    LoadingSpinner,
    StatusIndicator,

    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  props: {
    notLoaded: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      required: false,
    },
    id: {
      type: String,
      required: false,
    },
    owner: {
      type: Boolean,
      default: false,
    },
    canUploadBonding: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chooseGiftModal: false,
      creditsMedia: null,
      giftModal: false,
      gifts: [],
      iconChat: require('@/assets/images/icons/chat-blue.svg'),
      iconDiamonds1: require('@/assets/images/icons/diamonds-1.svg'),
      iconGift: require('@/assets/images/icons/gift.svg'),
      iconHeart: require('@/assets/images/icons/heart.svg'),
      iconHeartEmpty: require('@/assets/images/icons/heart-empty.svg'),
      iconLevelBronze: require('@/assets/images/levels/bronze.png'),
      iconLevelGold: require('@/assets/images/levels/gold.png'),
      iconLevelMember: require('@/assets/images/levels/member.png'),
      iconLevelSilver: require('@/assets/images/levels/silver.png'),
      iconLocation: require('@/assets/images/icons/location.svg'),
      iconPhone: require('@/assets/images/icons/phone-green.svg'),
      iconProfileEdit: require('@/assets/images/icons/profile-edit.svg'),
      iconProfileEditWhite: require('@/assets/images/icons/profile-edit-white.svg'),
      iconVideoCall2: require('@/assets/images/icons/video-call2.svg'),
      loading: false,
      modalBuyMedia: false,
      modalReportUser: false,
      multimediaCredits: [],
      notLoadedImage: require('@/assets/images/pages/error-dark.svg'),
      privateImage: require('@/assets/images/profile/private-image.jpeg'),
      privateImagesShowStatus: false,
      privateVideosShowStatus: false,
      report: {
        reason: '',
        description: '',
      },
      selectedGift: null,
      showGifts: false,
      slide: 0,
      sliding: null,
      // Moments
      moments: [],
      momentsLoading: false,
      momentsOffset: 0,
      momentsLimit: 12,
      momentsHasMore: true,
      selectedMoment: null,
      momentModal: false,
      momentMessageText: '',
      photoModal: false,
    }
  },
  computed: {
    // Keşfet kartlarındaki getCallMode ile aynı kural: AI -> sesli; cameraOn true -> görüntülü;
    // cameraOn false ve voiceOn true -> sesli; ikisi de kapalı -> arama butonu yok.
    // Alanlar hiç gelmezse Keşfet'teki gibi görüntülü gösterilir.
    profileCallMode() {
      const user = this.user || {}
      if (user.isAi || user.is_ai) return 'voice'
      const profile = user.profile || {}
      const pick = (...values) => values.find(v => v !== undefined && v !== null)
      const cameraOn = pick(profile.cameraOn, user.cameraOn, user.camera_on)
      const voiceOn = pick(profile.voiceOn, user.voiceOn, user.voice_on)
      if (cameraOn === undefined && voiceOn === undefined) return 'video'
      if (cameraOn) return 'video'
      if (voiceOn) return 'voice'
      return null
    },
    creditsPhoto() {
      return Array.isArray(this.multimediaCredits) ? this.multimediaCredits.find(credit => credit.productType === 'user_photo') : null
    },
    creditsVideo() {
      return Array.isArray(this.multimediaCredits) ? this.multimediaCredits.find(credit => credit.productType === 'user_video') : null
    },
    privateImages() {
      if (this.user && this.user.images && Array.isArray(this.user.images)) {
        return this.user.images.filter(i => i.hidden)
      }
      return []
    },
    privateVideos() {
      if (this.user && this.user.videos && Array.isArray(this.user.videos)) {
        return this.user.videos.filter(i => i.hidden)
      }
      return []
    },
    reportReasonTypes() {
      return this.$store.getters['app/reportReasonTypes']
    },
    userGender() {
      return this.$store.getters['auth/userGender']
    },
    userImages() {
      if (!this.user) return []

      const profileImageObj = {
        id: this.user.profile.imageUuid,
        secureUrl: this.user.profile.image,
      }

      const images = Array.isArray(this.user.images) ? this.user.images : []

      const filteredImages = images.filter(img => {
        if (img.hidden) return false

        const isSameId = img.id === this.user.profile.imageUuid
        const isSameUrl = img.secureUrl === this.user.profile.image

        return !isSameId && !isSameUrl
      })

      return [profileImageObj].concat(filteredImages)
    },
    socket() {
      return this.$store.getters['socket/chatConnection']
    },
    socketConnected() {
      return this.$store.getters['socket/connected']
    },
    currentUserId() {
      return this.$store.getters['auth/userId']
    },
  },
  watch: {
    user: {
      handler(newUser) {
        if (newUser?.id) {
          this.getMoments(true)
        }
      },
      immediate: true,
    },
  },
  created() {
    // Hediye satışı kaldırıldı
    // this.getGifts()
    // this.getMultimediaCredits()

    // Hediye satışı kaldırıldı — /profil/gifts derin bağlantısı modal açmıyor
    if (false && this.$route.params?.type === 'gifts') {
      this.chooseGiftModal = true
    }
  },
  methods: {
    back() {
      this.$router.back()
    },
    openPhotoModal() {
      if (!this.user || !this.user.profile || !this.user.profile.image) return
      this.slide = 0
      this.photoModal = true
    },
    // Moments methods
    async getMoments(reset = false) {
      if (this.momentsLoading) return
      if (!this.user?.id) return

      if (reset) {
        this.moments = []
        this.momentsOffset = 0
        this.momentsHasMore = true
      }

      if (!this.momentsHasMore) return

      this.momentsLoading = true
      try {
        const response = await useJwt.getUserPosts(this.user.id, this.momentsOffset, this.momentsLimit)
        const newMoments = response.data?.body || []

        if (newMoments.length < this.momentsLimit) {
          this.momentsHasMore = false
        }

        this.moments = [...this.moments, ...newMoments]
        this.momentsOffset += this.momentsLimit
      } catch (error) {
        console.error('Error fetching moments:', error)
      } finally {
        this.momentsLoading = false
      }
    },
    openMomentModal(moment) {
      this.selectedMoment = moment
      this.momentModal = true
    },
    closeMomentModal() {
      this.selectedMoment = null
      this.momentModal = false
      this.momentMessageText = ''
    },
    sendMomentMessage() {
      if (!this.momentMessageText.trim() || !this.selectedMoment) return

      if (!this.socket || !this.socketConnected) {
        showNotify(this.$toast, this.$t('Connecting to the system. Please wait.'), 'warning')
        return
      }

      const payload = {
        from: this.currentUserId,
        to: this.selectedMoment.uuid,
        content: this.momentMessageText.trim(),
        type: 'CHAT_MSG',
        source: 'moment',
        source_id: this.selectedMoment.id,
      }

      this.socket.emit('send', payload)
      showNotify(this.$toast, this.$t('Message sent successfully'), 'success')
      this.momentMessageText = ''
    },
    isVideo(mediaType) {
      return mediaType?.toLowerCase()?.includes('video')
    },
    toggleMomentLike(moment) {
      if (!moment) return

      const request = moment.is_liked
        ? useJwt.likePostDelete(moment.id)
        : useJwt.likePost({ post_id: moment.id })

      request
        .then(() => {
          moment.is_liked = !moment.is_liked
          moment.like_count = moment.is_liked ? moment.like_count + 1 : moment.like_count - 1
          
          // moments listesindeki ilgili öğeyi de güncelle
          const idx = this.moments.findIndex(m => m.id === moment.id)
          if (idx !== -1) {
            this.moments[idx].is_liked = moment.is_liked
            this.moments[idx].like_count = moment.like_count
          }
        })
        .catch(error => {
          console.error('Error toggling like:', error)
        })
    },
    deleteMoment(moment) {
      if (!moment) return

      this.$bvModal.msgBoxConfirm(this.$t('Are you sure you want to delete?'), {
        title: this.$t('Confirmation'),
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.$t('Delete'),
        cancelTitle: this.$t('Cancel'),
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      }).then(value => {
        if (!value) return

        this.loading = true
        useJwt.deletePost(moment.id)
          .then(() => {
            this.moments = this.moments.filter(m => m.id !== moment.id)
            showNotify(this.$toast, this.$t('Deleted successfully'), 'success')
            this.closeMomentModal()
          })
          .catch(error => {
            throwDefaultError(this.$toast, error)
          })
          .finally(() => {
            this.loading = false
          })
      })
    },
    blockUser() {
      this.$emit('setLoading', true)
      useJwt.blockUser(this.user.id)
        .then(response => {
          showNotify(this.$toast, this.$t('User blocking successful'), 'success')
          // this.$emit('setUser', {
          //   ...this.user,
          //   fav: true
          // })

          if (this.user && typeof this.user.blockedByUser !== 'undefined') {
            this.user.blockedByUser = true
          }

          gtmTrackNewEvent('blockuser', {
            category: 'Profil',
            action: 'BlockUser',
            label: this.user.id,
          })
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.$emit('setLoading', false) })
    },
    buyMedia(media) {
      this.$emit('setLoading', true)
      useJwt.sendGift({
        targetUserUuid: this.user.id,
        productId: media.type == 1 ? this.creditsPhoto.productId : this.creditsVideo.productId,
      })
        .then(response => {
          showNotify(this.$toast, this.$t('Media buying successful'), 'success')
          this.$emit('getUser')
          this.$emit('getMe')
          this.modalBuyMedia = false
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => {
          this.$emit('setLoading', false)
        })
    },
    buyMediaModal(media) {
      if (media.type == 1 && !media.secureUrl.includes('blur')) {
        const viewer = this.$el.querySelector('.private-images').$viewer
        viewer.show()
        return true
      }
      if (!media.hidden || (media.type == 1 && !this.creditsPhoto) || (media.type == 2 && !this.creditsVideo)) {
        return true
      }

      this.creditsMedia = media
      this.modalBuyMedia = true
    },
    call() {
      // Hedef kullanıcı AI ise yeni LiveKit sesli görüşme akışını kullan; mevcut Agora akışı aynen korunur.
      if (this.user && this.user.isAi) {
        this.$root.$emit('ai-voice-call:start', this.user)
        return
      }
      this.$store.dispatch('app/setModalCallType', 'audio')
      this.$store.dispatch('app/setCallDetails', {
        callSource: 'user_profile_view',
        userOrder: 0,
      })
      // modalCall önceden true kaldıysa izleyici tetiklenmiyordu; görüntülü aramadaki gibi false -> true
      this.$store.dispatch('app/setModalCall', false)
      setTimeout(() => {
        this.$store.dispatch('app/setModalCallUser', this.user)
        this.$store.dispatch('app/setModalCall', true)
      }, 33)
      // if (this.user?.profile?.callType == 2) {
      //   this.$store.dispatch('app/setModalDownloadMobileApp', true)
      // } else {
      //   if (!this.$store.getters['auth/userPhoneNumber']) {
      //     this.$store.dispatch('app/setModalAddPhoneNumberShowDiamond', false)
      //     this.$store.dispatch('app/setModalAddPhoneNumber', true)
      //     this.$store.dispatch('app/setVerificationType', 'phone')
      //   } else {
      //     this.$emit('setLoading', true)
      //     useJwt.callUser(this.user.id)
      //         .then(response => {
      //           showNotify(this.$toast, 'Arama başarılı', 'success')
      //
      //           gtmTrackNewEvent('call', {
      //             category: 'Profil',
      //             action: 'Call',
      //             label: this.user.id
      //           })
      //         })
      //         .catch(error => {
      //           const { response } = error
      //           throwDefaultError(this.$toast, error)
      //           if (response && response.data?.header?.error?.errorCode == 215) {
      //             this.$store.dispatch('app/setModalAddPhoneNumberShowDiamond', false)
      //             this.$store.dispatch('app/setModalAddPhoneNumber', true)
      //             this.$store.dispatch('app/setVerificationType', 'phone')
      //           }
      //         })
      //         .finally(() => { this.$emit('setLoading', false) })
      //   }
      // }
    },
    checkBadge(level) {
      if (!this.user) return true

      const userBadgeLevel = this.user.profile?.userBadgeLevel ? this.user.profile?.userBadgeLevel.toLowerCase() : ''

      if (level === 'member') {
        if (['boho_member', 'bronze_member', 'silver_member', 'gold_member'].includes(userBadgeLevel)) return false
      } else if (level === 'bronze') {
        if (['bronze_member', 'silver_member', 'gold_member'].includes(userBadgeLevel)) return false
      } else if (level === 'silver') {
        if (['silver_member', 'gold_member'].includes(userBadgeLevel)) return false
      } else if (level === 'gold') {
        if (['gold_member'].includes(userBadgeLevel)) return false
      }

      return true
    },
    getGifts(showLoading = false) {
      if (showLoading) {
        this.loading = true
      }
      useJwt.getGifts()
        .then(response => {
          this.gifts = response.data.body
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    getMultimediaCredits() {
      useJwt.getMultimediaCredits()
        .then(response => {
          this.multimediaCredits = response.data.body
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
    },
    getImgGift(name) {
      return require(`@/assets/images/gifts/${name}`)
    },
    getMe() {
      useJwt.getMe()
        .then(response => {
          store.dispatch('auth/updateUser', response.data.body)
        })
    },
    gtmGoChat() {
      gtmTrackNewEvent('chatclick', {
        category: 'Profil',
        action: 'ChatClick',
        label: this.user.id,
      })
    },
    hideGiftModal(showChooseAnotherGift = false) {
      this.selectedGift = null
      this.giftModal = false
      this.showGifts = false

      if (showChooseAnotherGift) this.chooseGiftModal = true
    },
    like() {
      this.$emit('setLoading', true)
      useJwt.addFavoriteUser(this.user.id)
        .then(response => {
          showNotify(this.$toast, this.$t('Liking successful'), 'success')
          this.$emit('setUser', {
            ...this.user,
            fav: true,
          })

          gtmTrackNewEvent('addfavorite', {
            category: 'Profil',
            action: 'AddFavorite',
            label: this.user.id,
          })
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.$emit('setLoading', false) })
    },
    numberFormat(value) {
      return numberFormat(value)
    },
    onSlideStart(slide) {
      this.sliding = true
    },
    onSlideEnd(slide) {
      this.sliding = false
    },
    reportUser() {
      // this.$refs.reportForm.validate().then(success => {
      //   if (success) {
      //     this.$emit('setLoading', true)
      //     this.loading = true
      //     const data = {
      //       reportee: this.$store.getters['auth/userName'],
      //       reporteeEmail: this.$store.getters['auth/userEmail'],
      //       reported: this.user.userName,
      //       reason: this.report.reason,
      //       description: this.report.description,
      //     }
      //     useJwt.reportUser(data)
      //       .then(() => {
      //         showNotify(this.$toast, this.$t('Reporting user successful'), 'success')
      //         this.modalReportUser = false
      //         this.report = {
      //           reason: '',
      //           description: '',
      //         }
      //
      //         gtmTrackNewEvent('reportuser', {
      //           category: 'Profil',
      //           action: 'ReportUser',
      //           label: this.user.id,
      //         })
      //       })
      //       .catch(error => {
      //         throwDefaultError(this.$toast, error)
      //       })
      //       .finally(() => {
      //         this.$emit('setLoading', false)
      //         this.loading = false
      //       })
      //   }
      // })

      // TODO: yeni şikayet sistemi
      this.loading = true

      const data = {
        related_id: null,
        reason: this.report.reason === 'Other' ? this.report.description : this.report.reason,
        report_source: 'profile',
        reported_uuid: this.user.id,
      }

      useJwt.reportUser2(data)
        .then(() => {
          showNotify(this.$toast, this.$t('Reporting user successful'), 'success')
          this.modalReportUser = false

          this.report = {
            reason: '',
            description: '',
          }

          gtmTrackNewEvent('reportuser', {
            category: 'Profil',
            action: 'ReportUser',
            label: this.user.id,
          })
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    showPrivateImages() {
      this.privateImagesShowStatus = true
      // const viewer = this.$el.querySelector('.private-images').$viewer
      // viewer.show()
    },
    showPrivateVideos() {
      this.privateVideosShowStatus = true
      // const viewer = this.$el.querySelector('.private-videos').$viewer
      // viewer.show()
    },
    showReportModal() {
      this.modalReportUser = true

      gtmTrackNewEvent('reportclick', {
        category: 'Profil',
        action: 'ReportClick',
        label: this.user.id,
      })
    },
    sendGift(gift) {
      this.loading = true
      this.$emit('setLoading', true)
      const data = {
        productId: gift.productId,
        targetUserUuid: this.user.id,
      }
      useJwt.sendGift(data)
        .then(response => {
          showNotify(this.$toast, this.$t('Gift sending successful'), 'success')
          this.hideGiftModal()
          this.getMe()

          gtmTrackNewEvent('giftsent', {
            category: 'Profil',
            action: 'GiftSent',
            label: this.user.id,
          })

          // Firebase Analytics Event
          if (window.firebaseAnalytics) {
            logEvent(window.firebaseAnalytics, 'web_gift_send', {
              fromId: this.$store.getters['auth/userId'],
              toId: this.user.id,
              giftType: gift.productId,
            })
          }
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => {
          this.loading = false
          this.$emit('setLoading', false)
        })
    },
    showGiftModal(gift) {
      this.selectedGift = gift
      this.showGifts = false
      this.chooseGiftModal = false
      // this.giftModal = true

      gtmTrackNewEvent('giftclick', {
        category: 'Profil',
        action: 'Giftclick',
        label: this.user.id,
      })

      this.sendGift(gift)
    },
    unBlockUser() {
      this.$emit('setLoading', true)
      useJwt.unBlockUser(this.user.id)
        .then(response => {
          showNotify(this.$toast, this.$t('Unblocking user successful'), 'success')
          // this.$emit('setUser', {
          //   ...this.user,
          //   fav: true
          // })

          if (this.user && typeof this.user.blockedByUser !== 'undefined') {
            this.user.blockedByUser = false
          }

          gtmTrackNewEvent('unblockuser', {
            category: 'Profil',
            action: 'UnblockUser',
            label: this.user.id,
          })
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.$emit('setLoading', false) })
    },
    unlike() {
      this.$emit('setLoading', true)
      useJwt.removeFavoriteUser(this.user.id)
        .then(response => {
          showNotify(this.$toast, this.$t('Unliking successful'), 'success')
          this.$emit('setUser', {
            ...this.user,
            fav: false,
          })

          gtmTrackNewEvent('removefavorite', {
            category: 'Profil',
            action: 'RemoveFavorite',
            label: this.user.id,
          })
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.$emit('setLoading', false) })
    },
    videoCall() {
      this.$store.dispatch('app/setModalCallType', 'video')
      this.$store.dispatch('app/setCallDetails', {
        callSource: 'user_profile_view',
        userOrder: 0,
      })
      this.$store.dispatch('app/setModalCall', false)
      setTimeout(() => {
        this.$store.dispatch('app/setModalCallUser', this.user)
        this.$store.dispatch('app/setModalCall', true)
      }, 33)
    },
    async goToStream() {
      // --- Video Room Logic --- 
      if (this.user.streamInfo && this.user.streamInfo.channelId) {
        const channelId = this.user.streamInfo.channelId;
        const publisherAgoraUid = this.user.streamInfo.agoraUid; // Get publisher's UID from the original user data
        
        if (!publisherAgoraUid) {
          showNotify(this.$toast, 'Yayıncı bilgisi eksik.', 'danger')
          return;
        }

        try {
          // Call the new service function
          const roomInfo = await subscribeVideoRoomService(channelId, publisherAgoraUid);

          // If service function succeeds, it returns the roomInfo object
          this.$store.dispatch('videoRoom/setRoomInfo', roomInfo); 
          
          this.$router.push({ 
            name: 'video-room', 
            params: {
              channelId: roomInfo.channelId, // Use channelId from returned roomInfo
              agoraUid: publisherAgoraUid    // Use PUBLISHER's agoraUid from streamInfo for the route param
            }
          }); 

        } catch (error) {
          console.error('Error subscribing to video room via service:', error);
          showNotify(this.$toast, error.message || 'Video yayınına katılırken beklenmedik bir sorun oluştu.', 'danger')
        }
        return; // Stop execution if video room logic was handled
      }

      // --- Voice Room Logic (Fallback) ---
      if (this.user.voiceRoomInfo && this.user.voiceRoomInfo.channelId) {
        const channelId = this.user.voiceRoomInfo.channelId;
        try {
          // Call the dedicated service function
          const roomInfo = await subscribeVoiceRoom(channelId);

          // If service call succeeds, it returns the roomInfo object
          this.$store.dispatch('voiceRoom/setRoomInfo', roomInfo); // Use dispatch or commit as appropriate

          // Navigate to the voice room page, using channelId from the returned roomInfo
          this.$router.push({ name: 'voice-room', params: { channelId: roomInfo.channelId } });

        } catch (error) {
          // Handle network or unexpected errors from the service
          console.error('Error subscribing to voice room via service:', error);
          showNotify(this.$toast, error.message || 'Ses odasına katılırken beklenmedik bir sorun oluştu.', 'danger')
        } finally {
          // Optional: Reset loading indicator state
          // this.isJoiningStream = false;
        }
      } else {
         // Handle case where neither info object is valid
         console.error('Cannot go to stream: No valid streamInfo or voiceRoomInfo found.');
         showNotify(this.$toast, 'Yayın bilgileri bulunamadı.', 'danger')
      }
    },
  },
}
</script>

<style lang="scss">
#user-profile {
  #user-profile-carousel {
    position: relative;

    .carousel-inner {
      border-radius: 24px;
      overflow: hidden;
    }

    .carousel-item {
      aspect-ratio: 3 / 4;
      max-height: 600px;

      @media (max-width: 1199px) {
        max-height: 500px;
      }

      @media (max-width: 767px) {
        max-height: 450px;
      }

      .img-overlay {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #f5f5f5;
      }

      .slide-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .carousel-control-prev,
    .carousel-control-next {
      width: auto;
      opacity: 1;
      top: 50%;
      transform: translateY(-50%);
    }

    .carousel-control-prev-icon,
    .carousel-control-next-icon {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      background-color: rgba(0, 0, 0, 0.65);
      background-size: 55% 55%;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
      border: 1px solid rgba(255, 255, 255, 0.4);
    }

    .carousel-control-prev-icon {
      margin-left: 10px;
    }

    .carousel-control-next-icon {
      margin-right: 10px;
    }

    .carousel-indicators {
      bottom: 10px;

      li {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: rgba(255, 255, 255, 0.5);
        border: none;
        margin: 0 3px;

        &.active {
          background-color: #F9447A;
        }
      }
    }
  }

  .gallery-edit-icon {
    position: absolute;
    z-index: 999;
    bottom: 24px;
    right: 24px;
    background-color: rgba(0, 0, 0, 0.15);
    border-radius: 50%;
    padding: 6px;
  }

  .bonding-upload-entry {
    margin: 18px 0 20px;

    .bonding-upload-entry-subtitle {
      margin-top: 6px;
      color: #6e6b7b;
      font-size: 13px;
      text-align: center;
    }
  }

  .card {
    background-color: #F5F8FD;
    border-radius: 24px;
  }

  .title {
    font-weight: 600;
    font-size: 22px;
    line-height: 28px;
    color: #000;
  }

  .user-information {

    .name-location {
      .location {
        font-weight: 400;
        font-size: 14px;
        line-height: 18px;
        color: #F9447A;
      }
    }

    .actions {
      display: flex;
      justify-content: end;
      align-items: start;
      margin-top: 6px;

      @media (max-width: 1199px) {
        justify-content: start;
      }

      div {
        background: #FFFFFF;
        box-shadow: 0 1px 8px #E8EDF4;
        border-radius: 50%;
        padding: 8px;
        margin-right: 12px;
        cursor: pointer;
      }
    }
  }

  .online-status {
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 8px;

    .content-type-badge {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 8px;
      font-size: 9px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;

      &.ai {
        background: linear-gradient(135deg, #8b5cf6, #a855f7);
        color: #fff;
      }

      &.real {
        background: linear-gradient(135deg, #22c55e, #4ade80);
        color: #fff;
      }
    }
  }

  .about-me {
    margin-top: 16px;
    background: #FFFFFF;
    box-shadow: 0 1px 8px #E8EDF4;
    border-radius: 12px;
    font-weight: 300;
    font-size: 14px;
    line-height: 25px;
    color: #3D3D3D;
    padding: 16px;

    p {
      margin-bottom: 6px;
    }

    .head {
      color: #000000;
      font-weight: 500;
      margin-bottom: 10px;
    }

    .link {
      color: #F9447A;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .images, .videos {
    margin-top: 20px;

    img {
      border-radius: 12px;
      cursor: zoom-in;
    }

    .plus {
      font-weight: 500;
      font-size: 14px;
      line-height: 18px;
      text-align: center;
      color: #F9447A;
      display: flex;
      align-items: center;
      justify-content: center;

      span {
        padding: 16px;
        background-color: #fff;
        border-radius: 50%;
        cursor: pointer;
      }
    }

    .private-video-play-icon {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: zoom-in;
    }
  }

  .user-level {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    margin-top: 12px;

    & > div {
      display: flex;
      justify-content: end;
      align-items: center;
      flex-direction: column;
      width: 25%;
      gap: 4px;
      height: 92px;

      div {
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        width: 100%;
      }

      &.user-level-bronze .bar {
        background: linear-gradient(to right, #CB6A34 0%, #F1AF8C 100%);
      }
      &.user-level-gold .bar {
        background: linear-gradient(to right, #D68E22 0%, #FFD75C 100%);
      }
      &.user-level-member .bar {
        background: linear-gradient(to right, #743BC5 0%, #DC85FF 100%);
      }
      &.user-level-silver .bar {
        background: linear-gradient(to right, #9A9A9A 0%, #D9D7DF 100%);
      }

      &.user-level-bronze .text {
        color: #CB6A34;
      }
      &.user-level-gold .text {
        color: #D68E22;
      }
      &.user-level-member .text {
        color: #743BC5;
      }
      &.user-level-silver .text {
        color: #212121;
      }

      &.passive {
        filter: grayscale(1);
        -webkit-filter: grayscale(1);

        &.user-level-silver .text {
          color: #7e7d7d;
        }
      }

      .bar {
        display: block;
        width: 100%;
        height: 8px;
      }

      .text {
        font-size: 8px;
        height: 26px;
        text-align: center;
        font-weight: bold;
      }

      &:first-of-type {
        .bar {
          border-bottom-left-radius: 12px;
          border-top-left-radius: 12px;
        }
      }
      &:last-of-type {
        .bar {
          border-bottom-right-radius: 12px;
          border-top-right-radius: 12px;
        }
      }
    }
  }
}
.modal-private-video {
  .modal-content {
    background-color: #fff;
  }
  .modal-body {
    padding: 12px !important;
    display: flex;
    justify-content: center;
  }
}

// Define the pulsing animation
@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(79, 187, 86, 0.7); // Match background color
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 5px rgba(79, 187, 86, 0); // Match background color
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(79, 187, 86, 0); // Match background color
  }
}

.live-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background-color: #4fbb56;
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  animation: pulse 1.5s infinite cubic-bezier(0.66, 0, 0, 1); // Apply the animation
  transition: background-color 0.2s;
  height: 24px; // Match other action icons height
  margin-left: -12px; // Adjust spacing if needed

  &:hover {
    background-color: darken(#5aff65, 10%);
  }
}

// Wrapper for positioning next to status indicator
.live-badge-wrapper {
  display: inline-flex; // Changed from flex to inline-flex
  align-items: center;
  vertical-align: middle; // Align with status indicator text
  cursor: pointer; 
}

// Moments Section Styles
.moments-section {
  margin-top: 20px;
  background: #FFFFFF;
  box-shadow: 0 1px 8px #E8EDF4;
  border-radius: 12px;
  padding: 16px;

  .section-title {
    font-weight: 600;
    font-size: 16px;
    color: #000;
  }

  .moments-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;

    @media (max-width: 575px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 4px;
    }
  }

  .moment-item {
    position: relative;
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.02);
    }

    .moment-media {
      width: 100%;
      height: 100%;
      position: relative;

      .moment-thumbnail {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .video-indicator {
        position: absolute;
        top: 8px;
        right: 8px;
        background: rgba(0, 0, 0, 0.6);
        color: #fff;
        padding: 4px 6px;
        border-radius: 4px;
        font-size: 10px;
      }
    }

    .moment-overlay {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
      padding: 20px 8px 8px;

      .like-count {
        color: #fff;
        font-size: 12px;
        display: flex;
        align-items: center;
        gap: 4px;

        svg {
          color: #F9447A;
        }
      }
    }
  }

  .no-moments {
    color: #999;
    font-size: 14px;
  }
}

// Moment Detail Modal
.moment-detail-modal {
  .modal-content {
    background: #000;
    border: none;
  }

  .modal-body {
    padding: 0 !important;
  }

  .moment-detail {
    position: relative;

    .moment-detail-close {
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 100;
      background: rgba(255, 255, 255, 0.9);
      color: #333;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.2s;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

      &:hover {
        background: #fff;
      }
    }

    .moment-detail-media {
      width: 100%;
      max-height: 70vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #000;

      .moment-detail-image,
      .moment-detail-video {
        max-width: 100%;
        max-height: 70vh;
        object-fit: contain;
      }
    }

    .moment-detail-info {
      background: #fff;
      padding: 16px;
      border-radius: 0 0 8px 8px;

      .moment-detail-content {
        font-size: 14px;
        color: #333;
        margin-bottom: 12px;
        line-height: 1.5;
      }

      .moment-detail-stats {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .like-stat {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          color: #666;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 20px;
          transition: background 0.2s;

          &:hover {
            background: #f5f5f5;
          }

          svg {
            font-size: 18px;
            color: #ccc;
            transition: color 0.2s, transform 0.2s;
          }

          svg.liked {
            color: #F9447A;
            transform: scale(1.1);
          }
        }

        .delete-stat {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          color: #ea5455;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 20px;
          transition: background 0.2s;

          &:hover {
            background: #fceaea;
          }
        }

        .date-stat {
          font-size: 12px;
        }
      }

      .moment-message-input {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid #eee;

        .message-input-field {
          flex: 1;
          background: #f5f5f5;
          border: none;
          border-radius: 20px;
          padding: 10px 16px;
          font-size: 14px;
          outline: none;
          transition: background 0.2s;

          &:focus {
            background: #eee;
          }

          &::placeholder {
            color: #999;
          }
        }

        .send-btn {
          width: 40px;
          height: 40px;
          background: #F9447A;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, transform 0.2s;

          svg {
            color: #fff;
            font-size: 16px;
          }

          &:hover {
            background: darken(#F9447A, 10%);
            transform: scale(1.05);
          }

          &.disabled {
            background: #ccc;
            cursor: not-allowed;

            &:hover {
              transform: none;
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
/* =========================================================
   Profil görüntüleme — yeni UI (sadece görünüm)
   ========================================================= */
$pv-muted: rgba(226, 220, 255, 0.72);

.pv-page {
  padding: 8px 28px 40px;
  color: #f4f1ff;

  @media (max-width: 991px) { padding: 6px 16px 32px; }
  @media (max-width: 575px) { padding: 4px 6px 24px; }
}

.pv-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 6px 0 18px;
}

.pv-round-btn {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(14, 10, 40, 0.6);
  border: 1px solid rgba(170, 140, 255, 0.35);
  cursor: pointer;
  transition: background .15s ease;

  &:hover { background: rgba(139, 92, 246, 0.3); color: #fff; }

  &.small { width: 34px; height: 34px; }
}

::v-deep .pv-dropdown-menu {
  background: #1e1648 !important;
  border: 1px solid rgba(170, 140, 255, 0.35) !important;
  border-radius: 14px !important;
  padding: 6px !important;

  .dropdown-item {
    color: #fff !important;
    border-radius: 10px;
    padding: 9px 12px;

    &:hover { background: rgba(139, 92, 246, 0.3) !important; }
  }
}

.pv-layout {
  max-width: 920px;
  margin: 0 auto;
}

.pv-card {
  position: relative;
  overflow: hidden;
  background: rgba(32, 22, 72, 0.72);
  border: 1px solid rgba(150, 110, 255, 0.28);
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(6, 2, 24, 0.45);
  margin-bottom: 20px;
}

.pv-info-col .pv-card:last-child { margin-bottom: 0; }

/* ---------- Galeri ---------- */
.pv-gallery-col {
  position: sticky;
  top: 90px;

  @media (max-width: 991px) { position: static; }
}

.pv-gallery-card {
  padding: 8px;
  border-color: rgba(234, 3, 178, 0.35);
  box-shadow: 0 12px 40px rgba(6, 2, 24, 0.45), 0 0 22px rgba(234, 3, 178, 0.15);

  ::v-deep #user-profile-carousel {
    .carousel-inner { border-radius: 16px; }

    .carousel-item .img-overlay { background: #0d0828; }

    .carousel-control-prev-icon,
    .carousel-control-next-icon {
      background-color: rgba(14, 10, 40, 0.7);
      border-color: rgba(170, 140, 255, 0.45);
    }

    .carousel-indicators li.active {
      background: linear-gradient(135deg, #ea03b2, #a855f7);
    }
  }

  ::v-deep .gallery-edit-icon {
    width: 40px;
    height: 40px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: linear-gradient(135deg, #8b5cf6, #a855f7);
    border: 3px solid #1d1247;
    box-shadow: 0 4px 14px rgba(139, 92, 246, 0.45);
  }
}

/* ---------- Hero ---------- */
.pv-hero {
  background: linear-gradient(135deg, #2b1a63 0%, #1d1247 45%, #3a1356 100%);
  /* Seçenekler menüsü kartın dışına taşabilsin; süsler .pv-hero-bg içinde kırpılır */
  overflow: visible;
}

.pv-hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: inherit;
}

.pv-hero-menu {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 5;

  [dir="rtl"] & {
    right: auto;
    left: 14px;
  }
}

.pv-hero-menu-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed 0%, #ea03b2 100%);
  border: 1px solid rgba(255, 120, 220, 0.45);
  box-shadow: 0 6px 18px rgba(234, 3, 178, 0.35);
  cursor: pointer;
  transition: filter .15s ease, transform .15s ease, box-shadow .15s ease;

  &:hover {
    filter: brightness(1.12);
    transform: scale(1.05);
    box-shadow: 0 8px 22px rgba(234, 3, 178, 0.5);
  }

  ::v-deep svg {
    color: #fff;
    stroke: #fff;
  }
}

.pv-hero-glow {
  position: absolute;
  top: -50%;
  right: -10%;
  width: 70%;
  height: 160%;
  background: radial-gradient(ellipse at 70% 40%, rgba(234, 3, 178, 0.32) 0%, rgba(147, 51, 234, 0.16) 40%, transparent 70%);
}

.pv-heart {
  position: absolute;
  overflow: visible;

  &.pv-heart-big {
    right: 5%;
    top: -18px;
    width: 120px;
    height: 120px;
    transform: rotate(14deg);
    filter: drop-shadow(0 0 16px rgba(234, 3, 178, 0.55));
    animation: pvHeartPulse 6s ease-in-out infinite;
  }

  &.pv-heart-small {
    right: 30%;
    top: 22px;
    width: 20px;
    height: 20px;
    filter: drop-shadow(0 0 8px rgba(234, 3, 178, 0.85));
    animation: pvHeartFloat 4.8s ease-in-out infinite;
  }
}

@keyframes pvHeartPulse {
  0%, 100% { opacity: 0.75; transform: translateY(0) rotate(14deg) scale(1); }
  50% { opacity: 1; transform: translateY(-6px) rotate(10deg) scale(1.06); }
}

@keyframes pvHeartFloat {
  0%, 100% { transform: translateY(0); opacity: 0.8; }
  50% { transform: translateY(-8px); opacity: 1; }
}

.pv-hero-content {
  position: relative;
  z-index: 1;
  padding: 28px 26px 26px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.pv-avatar-wrap {
  position: relative;
  margin-bottom: 14px;
}

.pv-avatar {
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  padding: 5px;
  border: 0;
  cursor: zoom-in;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(140deg, #ff5fb7, #a855f7 55%, #ff9f6b);
  box-shadow: 0 0 30px rgba(234, 3, 178, 0.4);
  transition: transform .2s ease, box-shadow .2s ease;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #1d1247;
  }

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 0 40px rgba(234, 3, 178, 0.6);
  }

  @media (max-width: 575px) {
    width: 124px;
    height: 124px;
  }
}

.pv-avatar-count {
  position: absolute;
  left: 50%;
  bottom: -6px;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: rgba(14, 10, 40, 0.85);
  border: 1px solid rgba(234, 3, 178, 0.45);
}

.pv-avatar-edit {
  position: absolute;
  right: 4px;
  bottom: 8px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 3px solid #1d1247;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: linear-gradient(135deg, #8b5cf6, #a855f7);
}

.pv-status-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 0 10px;
}

.pv-name {
  color: #fff !important;
  font-size: 32px;
  line-height: 38px;
  font-weight: 800;
  margin: 0 0 8px;
  word-break: break-word;

  .pv-age {
    font-weight: 500;
    color: rgba(255, 255, 255, 0.85);
  }

  @media (max-width: 575px) { font-size: 26px; line-height: 32px; }
}

.pv-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: $pv-muted;
  font-size: 15px;
  margin-bottom: 4px;
}

.pv-location svg { color: #ff4fa8; }

.pv-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 20px;
  width: 100%;

  @media (max-width: 575px) {
    flex-wrap: nowrap;
    gap: 8px;
  }
}

/* Keşfet kartlarındaki (DiscoveryGrid .card-actions-bar) buton görünümünün profil boyutuna uyarlanmış hali:
   yarı saydam cam efektli hap ve yuvarlak butonlar */
.pv-action {
  height: 46px;
  min-width: 46px;
  border-radius: 9999px;
  border: none;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 18px;
  cursor: pointer;
  text-decoration: none !important;
  transition: all .15s ease;

  &:hover { background: rgba(255, 255, 255, 0.3); color: #fff; }
  &:active { transform: scale(0.96); }

  @media (max-width: 575px) {
    height: 42px;
    min-width: 42px;
    font-size: 16px;
  }

  // Yazılı arama butonları (Sesli Arama / Görüntülü Arama)
  &.pv-action-pill {
    flex: 1 1 160px;
    max-width: 240px;
    min-width: 0;
    padding: 0 18px;
    font-size: 14px;
    font-weight: 700;

    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    @media (max-width: 575px) {
      flex: 1 1 0;
      padding: 0 10px;
      font-size: 12.5px;
      gap: 6px;
    }
  }

  &.pv-action-like.active {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
  }
}

/* ---------- Bölüm başlıkları ---------- */
.pv-about,
.pv-moments {
  padding: 20px 24px 22px;
}

.pv-section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.pv-section-title {
  display: flex;
  align-items: center;
  gap: 12px;

  h3 {
    color: #fff !important;
    font-size: 17px;
    font-weight: 700;
    margin: 0;
  }
}

.pv-section-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #8b5cf6, #a855f7);

  &.pink { background: linear-gradient(135deg, #ff4fa8, #c026d3); }
}

.pv-about {
  p {
    color: rgba(236, 232, 255, 0.86);
    font-size: 15px;
    line-height: 1.65;
    margin: 0;
    white-space: pre-line;
  }

  .bonding-upload-entry {
    margin: 16px 0 0;

    .bonding-upload-entry-subtitle { color: $pv-muted; }
  }
}

/* ---------- Seviyeler ---------- */
.pv-levels {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  padding: 16px 20px;

  @media (max-width: 575px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.pv-level {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &.passive {
    .pv-level-icon img { filter: grayscale(1); opacity: .55; }
    .pv-level-label { color: $pv-muted; }
    .pv-level-bar span { width: 0; }
  }
}

.pv-level-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pv-level-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(139, 92, 246, 0.25);

  img { width: 24px; height: 24px; object-fit: contain; }
}

.pv-level-label {
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.pv-level-bar {
  height: 6px;
  border-radius: 6px;
  background: rgba(139, 92, 246, 0.25);
  overflow: hidden;

  span {
    display: block;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, #8b5cf6, #ea03b2);
  }
}

/* ---------- Moments ---------- */
.pv-moments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.pv-moment {
  position: relative;
  aspect-ratio: 3 / 4;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  background: #0d0828;
  border: 1px solid rgba(170, 140, 255, 0.22);

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform .25s ease;
  }

  &:hover img,
  &:hover video { transform: scale(1.04); }
}

.pv-moment-play {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  background: rgba(0, 0, 0, 0.5);
}

.pv-moment-likes {
  position: absolute;
  left: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 9999px;
  color: #fff;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.45);

  svg { color: #ff4fa8; }
}

.pv-moments-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px 0 6px;
  color: #fff;

  svg {
    color: #ff6bb8;
    filter: drop-shadow(0 0 10px rgba(234, 3, 178, 0.6));
  }
}

.pv-load-more {
  border: 1px solid rgba(170, 140, 255, 0.35);
  background: rgba(14, 10, 40, 0.5);
  color: #fff;
  border-radius: 12px;
  padding: 9px 18px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { background: rgba(139, 92, 246, 0.25); }
  &:disabled { opacity: .6; }
}
</style>

<style lang="scss">
/* Büyük fotoğraf görünümü (modal body'ye taşındığı için global, .pv-photo-modal ile sınırlı) */
.pv-photo-modal {
  .modal-content {
    background: linear-gradient(160deg, #24175a 0%, #120b30 100%);
    border: 1px solid rgba(234, 3, 178, 0.35);
    border-radius: 22px;
    box-shadow: 0 24px 60px rgba(4, 1, 20, 0.65);
    overflow: hidden;
  }

  .modal-body { padding: 0; }

  .pv-photo-modal-inner { position: relative; }

  .pv-photo-close {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 20;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid rgba(170, 140, 255, 0.4);
    background: rgba(10, 6, 30, 0.75);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .pv-photo-slide {
    height: 78vh;
    max-height: 760px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #0d0828;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    @media (max-width: 575px) { height: 70vh; }
  }

  .carousel-control-prev,
  .carousel-control-next {
    width: auto;
    opacity: 1;
    padding: 0 12px;
  }

  .carousel-control-prev-icon,
  .carousel-control-next-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: rgba(14, 10, 40, 0.75);
    background-size: 55% 55%;
    border: 1px solid rgba(170, 140, 255, 0.45);
  }

  .carousel-indicators {
    bottom: 10px;

    li {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      border: 0;
      margin: 0 3px;
      background-color: rgba(255, 255, 255, 0.5);

      &.active { background: #ea03b2; }
    }
  }
}
</style>

<style lang="scss">
/* Profil 3 nokta menüsü: koyu Boho teması (kapsamsız ve yüksek öncelikli; önceden açık zemin + beyaz yazı kalıyordu) */
#user-profile .dropdown-menu.pv-dropdown-menu,
body .dropdown-menu.pv-dropdown-menu {
  min-width: 210px;
  padding: 6px !important;
  background: linear-gradient(180deg, #1f1035 0%, #150a28 100%) !important;
  border: 1px solid rgba(168, 85, 247, 0.35) !important;
  border-radius: 14px !important;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55) !important;

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px !important;
    border-radius: 10px;
    color: #f4f1ff !important;
    font-weight: 600;
    background: transparent !important;

    svg {
      color: #c4a8ff;
    }

    &:hover,
    &:focus {
      background: rgba(139, 92, 246, 0.28) !important;
      color: #fff !important;
    }
  }

  li:last-child .dropdown-item,
  li:last-child .dropdown-item svg {
    color: #fbbf24 !important;
  }
}
</style>
